/**
 * Entity Form Registry - Maps entity keys to their form components.
 * Used by QuickCreateMenu to render the appropriate form for each entity type.
 *
 * Only forms that can be opened with simple open/onClose props are included.
 * Forms that require additional context (e.g., onSubmit handlers, parent entities)
 * should be accessed from their respective pages.
 */

import React from 'react';

// Quick-create wrapper forms for tasks
import { QuickCreateTaskForm } from './QuickCreateTaskForm';

// User Management forms
import { UserForm } from '../../modules/user_management/components/UserForm';

// Contacts forms
import { OrganizationQuickCreateDialog } from '../../modules/master_data/contacts/components/OrganizationQuickCreateDialog';
import { ContactQuickCreateDialog } from '../../modules/master_data/contacts/components/ContactQuickCreateDialog';

// Sales forms
import { DealQuickCreateDialog } from '../../modules/pipeline/components/DealQuickCreateDialog';
import { SequenceQuickCreateDialog } from '../../modules/pipeline/components/prospecting/SequenceQuickCreateDialog';
import { EmailTemplateFormDialog } from '../../modules/pipeline/components/prospecting/EmailTemplateFormDialog';

// Recruiting forms
import { CandidateQuickCreateDialog } from '../../modules/recruiting/components/CandidateQuickCreateDialog';
import { JobQuickCreateDialog } from '../../modules/recruiting/components/JobQuickCreateDialog';
import { InterviewForm } from '../../modules/recruiting/components/InterviewForm';

// People forms
import { EmployeeQuickCreateDialog } from '../../modules/people/components/EmployeeQuickCreateDialog';

// Support forms
import { TicketQuickCreateDialog } from '../../modules/support/components/TicketQuickCreateDialog';
import { ArticleQuickCreateDialog } from '../../modules/support/components/ArticleQuickCreateDialog';

// AI Agents forms
import { AgentQuickCreateDialog } from '../../modules/ai_agents/components/AgentQuickCreateDialog';

// Products forms
import { ProductQuickCreateDialog } from '../../modules/master_data/products/components/ProductQuickCreateDialog';

// Inventory forms
import { WarehouseQuickCreateDialog } from '../../modules/inventory/components/WarehouseQuickCreateDialog';
import { QuickCreateStockReceiptForm } from './QuickCreateStockReceiptForm';
import { QuickCreateStockIssueForm } from './QuickCreateStockIssueForm';

// Procurement forms
import { VendorQuickCreateDialog } from '../../modules/procurement/components/VendorQuickCreateDialog';
import { PurchaseOrderQuickCreateDialog } from '../../modules/procurement/components/PurchaseOrderQuickCreateDialog';

// Quick-create wrapper forms (for forms that need onSubmit handlers)
import { QuickCreateJournalEntryForm } from './QuickCreateJournalEntryForm';
import { QuickCreateInvoiceForm } from './QuickCreateInvoiceForm';
import { QuickCreateExpenseForm } from './QuickCreateExpenseForm';
import { QuickCreatePaymentForm } from './QuickCreatePaymentForm';
import { QuickCreateDocumentUploadForm } from './QuickCreateDocumentUploadForm';

// Common form props interface
export interface EntityFormProps {
  open: boolean;
  onClose: () => void;
}

// Form component type
type FormComponent = React.ComponentType<EntityFormProps>;

// Registry entry that handles special rendering logic
interface FormRegistryEntry {
  component: FormComponent;
  // Some forms need special handling (e.g., ItemForm needs board selection)
  requiresContext?: 'board';
}

// Entity form registry - maps entity keys to their form configurations
// Note: Only forms with simple open/onClose signatures are included here.
// Forms like AccountForm and JournalEntryForm require onSubmit handlers
// and should be created from their respective pages.
const ENTITY_FORM_REGISTRY: Record<string, FormRegistryEntry> = {
  // Tasks
  // Note: Board and Workspace creation use dedicated pages for template support - handled via NAVIGATION_BASED_ENTITIES in QuickCreateMenu
  task: { component: QuickCreateTaskForm as FormComponent, requiresContext: 'board' },

  // User Management (admin module)
  user: { component: UserForm },

  // CRM
  company: { component: OrganizationQuickCreateDialog },
  contact: { component: ContactQuickCreateDialog },
  pipeline_deal: { component: DealQuickCreateDialog },
  pipeline_sequence: { component: SequenceQuickCreateDialog },
  pipeline_email_template: { component: EmailTemplateFormDialog },

  // Recruiting
  recruiting_job_posting: { component: JobQuickCreateDialog },
  recruiting_candidate: { component: CandidateQuickCreateDialog },
  recruiting_interview: { component: InterviewForm },

  // People
  people_employee: { component: EmployeeQuickCreateDialog },

  // Accounting (using wrapper forms that handle mutations internally)
  journal_entry: { component: QuickCreateJournalEntryForm },
  invoice: { component: QuickCreateInvoiceForm },
  bill: { component: QuickCreateExpenseForm },
  payment: { component: QuickCreatePaymentForm },
  accounting_document: { component: QuickCreateDocumentUploadForm },

  // Support
  // Only include frequently-created entities; config entities (SLA, routing rules) are setup-time only
  support_ticket: { component: TicketQuickCreateDialog },
  support_kb_article: { component: ArticleQuickCreateDialog },

  // AI Agents
  agent: { component: AgentQuickCreateDialog },

  // Products
  product: { component: ProductQuickCreateDialog },
  // Note: product_category uses inline tree component, not a dialog form

  // Inventory
  warehouse: { component: WarehouseQuickCreateDialog },
  stock_receipt: { component: QuickCreateStockReceiptForm },
  stock_issue: { component: QuickCreateStockIssueForm },

  // Procurement
  vendor_profile: { component: VendorQuickCreateDialog },
  purchase_order: { component: PurchaseOrderQuickCreateDialog },
};

/**
 * Get the form entry for an entity key
 */
export function getFormEntry(entityKey: string): FormRegistryEntry | undefined {
  return ENTITY_FORM_REGISTRY[entityKey];
}

/**
 * Check if an entity has a registered form suitable for quick-create
 */
export function hasRegisteredForm(entityKey: string): boolean {
  return entityKey in ENTITY_FORM_REGISTRY;
}

/**
 * Get all entity keys that have registered forms
 */
export function getRegisteredEntityKeys(): string[] {
  return Object.keys(ENTITY_FORM_REGISTRY);
}
